import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { Modal, Field, Badge, Empty } from './ui.jsx';
import { useToast } from './feedback.jsx';
import { t, getLang } from '../i18n/index.jsx';
import { IconPlus } from './icons.jsx';

const STATUS_TONE = { pending: 'gray', active: 'blue', done: 'green', skipped: 'gray' };
const STATUS_LABEL = {
  pending: 'Pending', active: 'Active', done: 'Done', skipped: 'Skipped',
};

function phaseLabel(phase) {
  return getLang() === 'it' ? phase.label_it : phase.label_en;
}

/**
 * Horizontal (mobile: vertical) stepper across a project's phase pipeline.
 * Tapping a phase opens its workspace: status, dates, notes and the
 * companies participating specifically in that phase.
 */
export default function PhaseStepper({ projectId, canEdit, companies = [] }) {
  const [phases, setPhases] = useState(null);
  const [open, setOpen] = useState(null);

  async function load() {
    const r = await api.get(`/projects/${projectId}/phases`);
    setPhases(r.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  if (!phases) return null;
  if (phases.length === 0) return null;

  return (
    <section className="panel phase-stepper-panel">
      <div className="panel-head">
        <h2 className="panel-title">{t('Phases')}</h2>
      </div>
      <div className="phase-stepper">
        {phases.map((ph) => (
          <button
            type="button"
            key={ph.id}
            className={`phase-step phase-${ph.status}`}
            onClick={() => setOpen(ph)}
          >
            <span className="phase-step-dot" />
            <span className="phase-step-label">{phaseLabel(ph)}</span>
            <Badge tone={STATUS_TONE[ph.status]}>{t(STATUS_LABEL[ph.status])}</Badge>
          </button>
        ))}
      </div>

      {open && (
        <PhaseModal
          phase={open}
          canEdit={canEdit}
          companies={companies}
          onClose={() => setOpen(null)}
          onSaved={() => { setOpen(null); load(); }}
        />
      )}
    </section>
  );
}

function PhaseModal({ phase, canEdit, companies, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({
    label_en: phase.label_en, label_it: phase.label_it, status: phase.status,
    started_at: phase.started_at || '', ended_at: phase.ended_at || '', notes: phase.notes || '',
  });
  const [participants, setParticipants] = useState(phase.participants || []);
  const [addingCompany, setAddingCompany] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save() {
    setBusy(true);
    try {
      await api.put(`/phases/${phase.id}`, form);
      toast(t('Phase saved'));
      onSaved();
    } catch (e) { toast(e.message, { tone: 'error' }); }
    finally { setBusy(false); }
  }

  async function addParticipant() {
    const companyId = Number(addingCompany);
    if (!companyId) return;
    const company = companies.find((c) => Number(c.id) === companyId);
    await api.post(`/phases/${phase.id}/participants`, { company_id: companyId });
    setParticipants((xs) => [...xs, { company_id: companyId, name: company?.name, type: company?.type }]);
    setAddingCompany('');
  }

  async function removeParticipant(companyId) {
    await api.del(`/phases/${phase.id}/participants/${companyId}`);
    setParticipants((xs) => xs.filter((x) => x.company_id !== companyId));
  }

  const availableCompanies = companies.filter(
    (c) => !participants.some((p) => Number(p.company_id) === Number(c.id))
  );

  return (
    <Modal title={phaseLabel(phase)} onClose={onClose}
      footer={canEdit ? (
        <div className="modal-actions">
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      ) : null}>
      {canEdit ? (
        <>
          <div className="grid-2">
            <Field label={t('Label (EN)')}><input value={form.label_en} onChange={set('label_en')} /></Field>
            <Field label={t('Label (IT)')}><input value={form.label_it} onChange={set('label_it')} /></Field>
          </div>
          <Field label={t('Status')}>
            <select value={form.status} onChange={set('status')}>
              {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{t(STATUS_LABEL[s])}</option>)}
            </select>
          </Field>
          <div className="grid-2">
            <Field label={t('Started')}><input type="date" value={form.started_at || ''} onChange={set('started_at')} /></Field>
            <Field label={t('Ended')}><input type="date" value={form.ended_at || ''} onChange={set('ended_at')} /></Field>
          </div>
          <Field label={t('Notes')}><textarea rows={3} value={form.notes} onChange={set('notes')} /></Field>
        </>
      ) : (
        <div className="detail-grid" style={{ marginBottom: 12 }}>
          <div className="kv"><span>{t('Status')}</span><b><Badge tone={STATUS_TONE[phase.status]}>{t(STATUS_LABEL[phase.status])}</Badge></b></div>
          {phase.started_at && <div className="kv"><span>{t('Started')}</span><b>{phase.started_at}</b></div>}
          {phase.ended_at && <div className="kv"><span>{t('Ended')}</span><b>{phase.ended_at}</b></div>}
        </div>
      )}

      <div className="phase-participants" style={{ marginTop: 14 }}>
        <div className="field-label">{t('Companies in this phase')}</div>
        {participants.length === 0 ? (
          <Empty>{t('No companies added to this phase yet.')}</Empty>
        ) : (
          <ul className="line-list">
            {participants.map((pp) => (
              <li key={pp.company_id}>
                <span className="ll-title">{pp.name}</span>
                {canEdit && (
                  <button className="icon-btn xs" title={t('Remove')} onClick={() => removeParticipant(pp.company_id)}>×</button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && availableCompanies.length > 0 && (
          <div className="checklist-add" style={{ marginTop: 8 }}>
            <select value={addingCompany} onChange={(e) => setAddingCompany(e.target.value)}>
              <option value="">{t('Add a company…')}</option>
              {availableCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button className="btn ghost sm" type="button" onClick={addParticipant}>
              <IconPlus width={13} height={13} /> {t('Add')}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
